import {
  forwardRef,
  type MouseEventHandler,
  type PropsWithChildren,
} from 'react';

import classNames from 'classnames';

import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';
import { Icon, type IconName } from '@iconik-io/uad';

import type { TooltipWrappedProps } from '../../components/tooltip_trigger/tooltip_trigger';
import type { IconType } from '../../icons/constants';
import LegacyIcon from '../../icons/icon';
import { ui } from '../../utils/helpers';
import type { ButtonSize, ButtonVariant } from './types';

import mixins from '../../styles/core/mixins.module.css';
import styles from './button.module.css';

type Side = 'top' | 'right' | 'bottom' | 'left';
const sizeStyles: { [size in ButtonSize]: string } = {
  s: styles.size_s,
  m: styles.size_m,
};

// TODO: UAD Icons, delete iconSizeStyles after UAD icon migration
const iconSizeStyles: { [size in ButtonSize]: string } = {
  s: styles.icon_size_s,
  m: styles.icon_size_m,
};

export type ButtonProps = {
  type?: 'submit' | 'reset' | 'button';
  className?: string;
  noBorder?: boolean;
  variant?: ButtonVariant;
  disabled?: boolean;
  wideBasis?: boolean;
  active?: boolean;
  size?: ButtonSize;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  // TODO: UAD Icons, delete icon prop after UAD icon migration
  icon?: IconType;
  iconName?: IconName;
  name?: string;
  value?: string | string[] | number;
  // UI tests identifier
  uiKey?: string;
  // The arrow pointer for the button
  arrow?: Side;
  // Segment ID to select the corresponding segment in the player
  dataSegmentHandler?: string;
  // Segment start time for the corresponding segment in the player
  dataSegmentStart?: number | null;
  // Segment end time for the corresponding segment in the player
  dataSegmentEnd?: number | null;
  aria?: AriaProps;
  onMouseDown?: MouseEventHandler<HTMLButtonElement>;
} & TooltipWrappedProps;

const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      type = 'button',
      className,
      noBorder = false,
      variant = 'success',
      disabled,
      wideBasis = true,
      active = false,
      size = 'm',
      onClick,
      icon,
      iconName,
      children,
      name,
      value,
      arrow,
      dataSegmentHandler,
      dataSegmentStart,
      dataSegmentEnd,
      uiKey,
      onMouseOver,
      onMouseLeave,
      onMouseDown,
      'data-tooltip': dataTooltip,
      aria,
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      name={name}
      value={value}
      {...ui(uiKey)}
      className={classNames(
        className,
        styles.root,
        styles[variant],
        sizeStyles[size],
        {
          [styles.no_border]: noBorder,
          [styles.wide_basis]: wideBasis,
          [styles.only_icon]: !children,
          [styles.active]: active,
          [styles.arrow]: Boolean(arrow),
          [styles.has_icon]: iconName && children,
        },
        arrow ? styles[`arrow_${arrow}`] : undefined,
      )}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      data-tooltip={dataTooltip}
      data-segment-handler={dataSegmentHandler}
      data-segment-start={dataSegmentStart}
      data-segment-end={dataSegmentEnd}
      {...ariaToReactProps(aria)}
    >
      {iconName && (
        <Icon
          name={iconName}
          className={classNames(styles.icon, mixins.text_align_icon, {
            [styles.icon_offset_uad]: Boolean(children),
          })}
          size={size === 'm' ? 's' : 'xs'}
        />
      )}
      {!iconName && icon && (
        <LegacyIcon
          className={classNames(styles.icon, iconSizeStyles[size], {
            [styles.icon_offset]: Boolean(children),
          })}
          icon={icon}
          textAlign
        />
      )}
      {children}
    </button>
  ),
);

export default Button;
